import { useEntities, useEntity } from "@ui/hooks/serviceUtils";
import useDataManager, { DataManagerProps } from "@ui/hooks/useDataManager";
import { IMealBooking } from "./useMealBooking";
import { DateTime } from "luxon";
import { IFeedback } from "./useFeedBack";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IAdhocServing } from "./useAdhocServing";

export interface IMealServing {
  id: string;
  mealBooking: { id: string };
  servedAt: DateTime;
  feedback?: IFeedback;
}

type EmployeeMealData = {
  bookings: IMealBooking[];
  adhocs: IAdhocServing[];
};

export interface EmployeeMealSummary {
  meal: string;
  count: number;
}

const key = "mealServing";
const endpoint = "/api/mealserving";

export const useMealServingsQuery = (
  props: DataManagerProps<IMealServing> = {},
) => useDataManager<IMealServing>(key, endpoint + "/query", props);
export const useMealServings = () => useEntities<IMealServing>(key, endpoint);
export const useMealServing = (
  id: string | undefined | null,
  toast: boolean = true,
) =>
  useEntity<IMealServing>(
    key,
    endpoint,
    id,
    toast
      ? {
          add: "Meal Serving added",
          update: "Meal Serving updated",
          delete: "Meal Serving deleted",
        }
      : undefined,
  );

export const useRecentMealServings = (
  employeeId?: string,
  options?: { enabled?: boolean },
) =>
  useQuery<IMealServing[]>({
    queryKey: ["meal-servings", "recent", employeeId],
    queryFn: async () => {
      const url = `${endpoint}/recent${employeeId ? `?employeeId=${employeeId}` : ""}`;
      const response = await axios.get(url);
      return response.data;
    },
    enabled: options?.enabled ?? !!employeeId,
  });

export const useServedMeals = (date: DateTime, meal: string) => {
  const formattedDate =
    date instanceof DateTime
      ? date.toISODate()
      : date
        ? DateTime.fromJSDate(date).toISODate()
        : null;

  return useQuery({
    queryKey: [key, "served", formattedDate, meal],
    queryFn: async () => {
      const url = `${endpoint}/meal-serving-filter?date=${formattedDate}&meal=${meal}`;
      const { data } = await axios.get<IMealServing[]>(url);
      return data;
    },
    enabled: Boolean(formattedDate && meal),
  });
};

export const useMealsCounts = (date: DateTime, meal: string) => {
  const formattedDate =
    date instanceof DateTime
      ? date.toISODate()
      : date
        ? DateTime.fromJSDate(date).toISODate()
        : null;

  return useQuery<any, Error>({
    queryKey: [key, meal, "booking", formattedDate],
    queryFn: async () => {
      const url = `${endpoint}/counts?date=${formattedDate}&meal=${encodeURIComponent(meal || "")}`;
      const { data } = await axios.get<any>(url);

      return data;
    },
    enabled: Boolean(formattedDate && meal),
  });
};

export const useEmployeeMealData = (
  date: DateTime,
  meal: string,
  userId: string | undefined,
) => {
  const formattedDate = date.toISODate() ?? "";

  return useQuery<EmployeeMealData | null, Error>({
    queryKey: ["employee-meal-data", meal, "booking", formattedDate, userId],
    queryFn: async () => {
      if (!userId || !formattedDate || !meal) return null;

      const url = `${endpoint}/employee-meal-data?date=${encodeURIComponent(
        formattedDate,
      )}&meal=${encodeURIComponent(meal)}&employeeId=${encodeURIComponent(
        userId,
      )}`;

      const { data } = await axios.get<any>(url);

      return data;
    },
    enabled: Boolean(date && meal && userId),
  });
};

export const useEmployeeMealSummary = (
  startDate: DateTime | undefined,
  endDate: DateTime | undefined,
  employeeId: string | undefined,
) => {
  const formattedStart = startDate?.toISODate() ?? "";
  const formattedEnd = endDate?.toISODate() ?? "";

  return useQuery<EmployeeMealSummary[]>({
    queryKey: [key, formattedStart, formattedEnd, employeeId],
    queryFn: async () => {
      const url = `${endpoint}/employee-meal-summary?startDate=${formattedStart}&endDate=${formattedEnd}&employeeId=${employeeId}`;
      const { data } = await axios.get<EmployeeMealSummary[]>(url);
      return data;
    },
    enabled: Boolean(formattedStart && formattedEnd && employeeId),
  });
};
